import * as React from "react";
import { useState } from "react";
import { Input } from "./ui/input";
import { Button } from "./ui/button";
import { services } from "../services"; // your existing axios wrapper
import { AuthResponse, LoginPageProps, APIError } from "../types/login/login";
import loginImg from "../../src/assets/rwa_banner.png";

const LoginPage: React.FC<LoginPageProps> = ({ onLogin }) => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError("");

    try {
      const response = await services.post<AuthResponse>("/admin/login", {
        username: email, // Notice: your backend expects username
        password: password,
      });

      if (typeof response === "string" || response instanceof Error) {
        throw new Error("Authentication failed");
      }

      if (response.status < 200 || response.status >= 300) {
        throw new Error(response.statusText);
      }

      const { token, user } = response.data.data;

      // Save token and user details
      localStorage.setItem("token", token);
      localStorage.setItem("user", JSON.stringify(user));

      onLogin();
    } catch (err) {
      const error = err as APIError;
      setError(error.data?.message || "An error occurred");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="dark min-h-screen flex flex-col items-center justify-center bg-background text-foreground p-4">
      {/* Heading */}
      <h1 className="text-3xl text-black md:text-4xl font-bold mb-8 text-center">
        Welcome to Bloc<span className="text-green-500">Estate</span>
      </h1>

      {/* Card */}
      <div className="flex w-full max-w-4xl shadow-lg border border-border rounded-xl overflow-hidden">
        {/* Left side - Image */}
        <div className="hidden md:block md:w-1/2 bg-primary/10">
          <div className="h-full flex items-center justify-center p-6">
            <img
              src={loginImg}
              alt="Login Illustration"
              className="object-contain max-h-80 w-auto"
            />
          </div>
        </div>

        {/* Right side - Login Form */}
        <div className="w-full md:w-1/2 p-8 bg-card surface-2">
          <div className="mb-6">
            <h2 className="text-xl font-semibold text-center tracking-tight text-black">
              Sign in
            </h2>
            <p className="text-center text-muted-foreground mt-1 text-sm">
              Please enter your credentials
            </p>
          </div>

          <form className="space-y-5" onSubmit={submit}>
            <div className="space-y-4">
              <div className="space-y-2">
                <label
                  htmlFor="username"
                  className="text-sm font-medium text-black"
                >
                  Username
                </label>
                <Input
                  id="username"
                  name="username"
                  type="text"
                  autoComplete="username"
                  required
                  placeholder="Enter your username"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="w-full"
                />
              </div>

              <div className="space-y-2">
                <label
                  htmlFor="password"
                  className="text-sm font-medium text-black"
                >
                  Password
                </label>
                <Input
                  id="password"
                  name="password"
                  type="password"
                  autoComplete="current-password"
                  required
                  placeholder="Enter your password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="w-full"
                />
              </div>
            </div>

            {error && (
              <div className="text-destructive text-sm text-center bg-destructive/10 p-2 rounded-md">
                {error}
              </div>
            )}

            <Button
              type="submit"
              variant="gradient"
              className="w-full"
              disabled={loading}
            >
              {loading ? (
                <span className="flex items-center justify-center">
                  <svg
                    className="animate-spin -ml-1 mr-2 h-4 w-4 text-white"
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                  >
                    <circle
                      className="opacity-25"
                      cx="12"
                      cy="12"
                      r="10"
                      stroke="currentColor"
                      strokeWidth="4"
                    ></circle>
                    <path
                      className="opacity-75"
                      fill="currentColor"
                      d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                    ></path>
                  </svg>
                  Signing in...
                </span>
              ) : (
                "Sign in"
              )}
            </Button>
          </form>
        </div>
      </div>
    </div>
  );
};

export default LoginPage;
